// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {LongPressable} from '@app/features/app/components/LongPressable';
import {useHover} from '@app/features/app/hooks/useHover';
import {
	createMessageActionHandlers,
	isClientSystemMessage,
	useMessagePermissions,
} from '@app/features/channel/components/MessageActionUtils';
import styles from '@app/features/channel/components/MessageReactions.module.css';
import {EmojiInfoBottomSheet} from '@app/features/emoji/components/bottomsheets/EmojiInfoBottomSheet';
import {EmojiPickerPopout} from '@app/features/emoji/components/popouts/EmojiPickerPopout';
import Emoji from '@app/features/emoji/state/Emoji';
import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import {ExpressionPickerSheet} from '@app/features/expressions/components/modals/ExpressionPickerSheet';
import * as ReactionCommands from '@app/features/messaging/commands/ReactionCommands';
import {ReactionTooltip} from '@app/features/messaging/components/popouts/ReactionTooltip';
import {useNSFWMedia} from '@app/features/messaging/hooks/useNSFWMedia';
import type {Message} from '@app/features/messaging/models/Message';
import {getEmojiName, getReactionKey, useEmojiURL} from '@app/features/messaging/utils/ReactionUtils';
import nsfwStyles from '@app/features/theme/styles/NSFWBlur.module.css';
import {EmojiContextMenuItems} from '@app/features/ui/action_menu/items/EmojiContextMenuItems';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Popout} from '@app/features/ui/popover/Popout';
import KeyboardMode from '@app/features/ui/state/KeyboardMode';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import type {MessageReaction} from '@app/vendor/schema/domains/message/MessageResponseSchemas';
import {plural} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {SmileyIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useRef, useState} from 'react';

interface EmojiInfoData {
	id?: string;
	name: string;
	animated?: boolean;
}
const MessageReactionItem = observer(
	({
		message,
		reaction,
		isPreview = false,
		disableInteraction = false,
	}: {
		message: Message;
		reaction: MessageReaction;
		isPreview?: boolean;
		disableInteraction?: boolean;
	}) => {
		const {t, i18n} = useLingui();
		const [hoverRef, isHovering] = useHover();
		const [prevCount, setPrevCount] = useState(reaction.count);
		const [animationSyncKey, setAnimationSyncKey] = useState(0);
		const [emojiInfoOpen, setEmojiInfoOpen] = useState(false);
		const [selectedEmoji, setSelectedEmoji] = useState<EmojiInfoData | null>(null);
		const [tooltipHovering, setTooltipHovering] = useState(false);
		const isMobile = MobileLayout.isMobileLayout();
		const handleTooltipAnimationSync = useCallback(() => {
			setAnimationSyncKey((prev) => prev + 1);
		}, []);
		useEffect(() => {
			if (prevCount !== reaction.count) {
				setPrevCount(reaction.count);
			}
		}, [reaction.count, prevCount]);
		const isDisabled = isPreview || disableInteraction;
		const handleClick = () => {
			if (isDisabled) {
				return;
			}
			if (reaction.me) {
				ReactionCommands.removeReaction(i18n, message.channelId, message.id, reaction.emoji);
			} else {
				ReactionCommands.addReaction(i18n, message.channelId, message.id, reaction.emoji);
			}
		};
		const handleLongPress = () => {
			if (isDisabled) {
				return;
			}
			setSelectedEmoji({
				id: reaction.emoji.id ?? undefined,
				name: reaction.emoji.name,
				animated: reaction.emoji.animated,
			});
			setEmojiInfoOpen(true);
		};
		const handleCloseEmojiInfo = useCallback(() => {
			setEmojiInfoOpen(false);
			setSelectedEmoji(null);
		}, []);
		const emojiRecord = reaction.emoji.id ? Emoji.getEmojiById(reaction.emoji.id) : null;
		const handleContextMenu = (e: React.MouseEvent) => {
			if (isDisabled) return;
			if (!reaction.emoji.id) return;
			e.preventDefault();
			e.stopPropagation();
			const emojiForMenu: FlatEmoji = emojiRecord ?? {
				id: reaction.emoji.id,
				name: reaction.emoji.name,
				uniqueName: reaction.emoji.name,
				allNamesString: `:${reaction.emoji.name}:`,
				animated: Boolean(reaction.emoji.animated),
			};
			ContextMenuCommands.openFromEvent(e, ({onClose}) => (
				<EmojiContextMenuItems emoji={emojiForMenu} onClose={onClose} />
			));
		};
		const emojiName = getEmojiName(reaction.emoji);
		const emojiUrl = useEmojiURL({emoji: reaction.emoji, isHovering: isHovering || tooltipHovering});
		const isUnicodeEmoji = reaction.emoji.id == null;
		const isNSFW = !!emojiRecord?.nsfw;
		const {shouldBlur: reactionShouldBlur, shouldBlock: reactionShouldBlock} = useNSFWMedia(isNSFW, message.channelId);
		const variants = {
			up: {y: -20, opacity: 0},
			down: {y: 20, opacity: 0},
			center: {y: 0, opacity: 1},
		};
		const reactionCountText = plural(
			{count: reaction.count},
			{
				one: '# reaction',
				other: '# reactions',
			},
		);
		const actionText = reaction.me ? t`press to remove reaction` : t`press to add reaction`;
		const ariaLabel = isDisabled
			? t`${emojiName}: ${reactionCountText}`
			: t`${emojiName}: ${reactionCountText}, ${actionText}`;
		const buttonContent = (
			<FocusRing offset={-2}>
				<button
					type="button"
					className={clsx(styles.reactionButton, isDisabled && styles.reactionButtonDisabled)}
					aria-label={ariaLabel}
					aria-pressed={isDisabled ? undefined : reaction.me}
					aria-disabled={isDisabled || undefined}
					disabled={disableInteraction}
					tabIndex={isDisabled ? -1 : undefined}
					onClick={handleClick}
					onContextMenu={handleContextMenu}
				>
					<div className={styles.reactionInner}>
						{emojiUrl ? (
							reactionShouldBlock ? null : (
								<img
									src={emojiUrl}
									alt={emojiName}
									draggable={false}
									className={clsx('emoji', styles.emoji, reactionShouldBlur && nsfwStyles.nsfwBlurred)}
								/>
							)
						) : isUnicodeEmoji ? (
							<span className={clsx('emoji', styles.emoji)}>{reaction.emoji.name}</span>
						) : null}
						<div className={styles.countWrapper}>
							<AnimatePresence initial={false}>
								<motion.div
									key={reaction.count}
									initial={reaction.count > prevCount ? 'up' : 'down'}
									animate="center"
									exit={reaction.count > prevCount ? 'down' : 'up'}
									variants={variants}
									transition={{duration: Accessibility.useReducedMotion ? 0 : 0.2}}
								>
									{reaction.count}
								</motion.div>
							</AnimatePresence>
						</div>
					</div>
				</button>
			</FocusRing>
		);
		const containerClassName = clsx(
			styles.reactionContainer,
			reaction.me && styles.reactionMe,
			isDisabled && styles.reactionContainerDisabled,
		);
		if (isDisabled) {
			return <div className={containerClassName}>{buttonContent}</div>;
		}
		if (isMobile) {
			return (
				<LongPressable className={containerClassName} onLongPress={handleLongPress}>
					{buttonContent}
					<EmojiInfoBottomSheet isOpen={emojiInfoOpen} onClose={handleCloseEmojiInfo} emoji={selectedEmoji} />
				</LongPressable>
			);
		}
		return (
			<ReactionTooltip
				message={message}
				reaction={reaction}
				hoveredEmojiUrl={emojiUrl}
				animationSyncKey={animationSyncKey}
				onRequestAnimationSync={handleTooltipAnimationSync}
				onTooltipHoverChange={setTooltipHovering}
			>
				<div className={containerClassName} ref={hoverRef}>
					{buttonContent}
				</div>
			</ReactionTooltip>
		);
	},
);
export const MessageReactions = observer(
	({
		message,
		isPreview = false,
		onPopoutToggle,
	}: {
		message: Message;
		isPreview?: boolean;
		onPopoutToggle?: (isOpen: boolean) => void;
	}) => {
		const {t, i18n} = useLingui();
		const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
		const addReactionButtonRef = useRef<HTMLButtonElement>(null);
		const permissions = useMessagePermissions(message);
		const handlers = createMessageActionHandlers(message, {i18n});
		const keyboardModeEnabled = KeyboardMode.keyboardModeEnabled;
		const isMobileLayout = MobileLayout.isMobileLayout();
		const disableReactionInteraction = isClientSystemMessage(message);
		const blurReactionTrigger = useCallback(() => {
			if (keyboardModeEnabled) {
				return;
			}
			requestAnimationFrame(() => addReactionButtonRef.current?.blur());
		}, [keyboardModeEnabled]);
		const handleEmojiPickerToggle = useCallback(
			(open: boolean) => {
				setEmojiPickerOpen(open);
				onPopoutToggle?.(open);
				if (!open) {
					blurReactionTrigger();
				}
			},
			[onPopoutToggle, blurReactionTrigger],
		);
		const handleEmojiPickerOpen = useCallback(() => handleEmojiPickerToggle(true), [handleEmojiPickerToggle]);
		const handleEmojiPickerClose = useCallback(() => handleEmojiPickerToggle(false), [handleEmojiPickerToggle]);
		useEffect(() => {
			return () => {
				if (emojiPickerOpen) {
					onPopoutToggle?.(false);
				}
			};
		}, [emojiPickerOpen, onPopoutToggle]);
		const hasReactions = message.reactions.length > 0;
		return (
			<div className={styles.reactionsGrid}>
				{message.reactions.map((reaction) => (
					<MessageReactionItem
						key={getReactionKey(message.id, reaction.emoji)}
						message={message}
						reaction={reaction}
						isPreview={isPreview}
						disableInteraction={disableReactionInteraction}
					/>
				))}
				{hasReactions &&
					permissions?.canAddReactions &&
					!disableReactionInteraction &&
					!isPreview &&
					(isMobileLayout ? (
						<>
							<FocusRing offset={-2}>
								<button
									ref={addReactionButtonRef}
									type="button"
									className={clsx(styles.addReactionButton, emojiPickerOpen && styles.addReactionButtonActive)}
									aria-label={t`Add Reaction`}
									data-action="message-add-reaction-button"
									onClick={handleEmojiPickerOpen}
								>
									<SmileyIcon size={20} weight="fill" />
								</button>
							</FocusRing>
							<ExpressionPickerSheet
								isOpen={emojiPickerOpen}
								onClose={handleEmojiPickerClose}
								channelId={message.channelId}
								onEmojiSelect={handlers.handleEmojiSelect}
								visibleTabs={['emojis']}
							/>
						</>
					) : (
						<Popout
							render={({onClose}) => (
								<EmojiPickerPopout
									channelId={message.channelId}
									handleSelect={handlers.handleEmojiSelect}
									onClose={onClose}
								/>
							)}
							position="right-start"
							uniqueId={`emoji_picker-reactions-${message.id}`}
							shouldAutoUpdate={false}
							animationType="none"
							onOpen={handleEmojiPickerOpen}
							onClose={handleEmojiPickerClose}
						>
							<FocusRing offset={-2}>
								<button
									ref={addReactionButtonRef}
									type="button"
									className={clsx(styles.addReactionButton, emojiPickerOpen && styles.addReactionButtonActive)}
									aria-label={t`Add Reaction`}
									data-action="message-add-reaction-button"
								>
									<SmileyIcon size={20} weight="fill" />
								</button>
							</FocusRing>
						</Popout>
					))}
			</div>
		);
	},
);
